/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationUserSessionIssuerCimdClientsRequest,
  ListOrganizationUserSessionIssuerCimdClientsSecurity,
} from "../models/operations/listorganizationusersessionissuercimdclients.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationUserSessionIssuerCimdClientsInfiniteQuery,
  buildOrganizationUserSessionIssuerCimdClientsQuery,
  OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
  OrganizationUserSessionIssuerCimdClientsPageParams,
  OrganizationUserSessionIssuerCimdClientsQueryData,
  prefetchOrganizationUserSessionIssuerCimdClients,
  prefetchOrganizationUserSessionIssuerCimdClientsInfinite,
  queryKeyOrganizationUserSessionIssuerCimdClients,
  queryKeyOrganizationUserSessionIssuerCimdClientsInfinite,
} from "./organizationUserSessionIssuerCimdClients.core.js";
export {
  buildOrganizationUserSessionIssuerCimdClientsInfiniteQuery,
  buildOrganizationUserSessionIssuerCimdClientsQuery,
  type OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
  type OrganizationUserSessionIssuerCimdClientsPageParams,
  type OrganizationUserSessionIssuerCimdClientsQueryData,
  prefetchOrganizationUserSessionIssuerCimdClients,
  prefetchOrganizationUserSessionIssuerCimdClientsInfinite,
  queryKeyOrganizationUserSessionIssuerCimdClients,
  queryKeyOrganizationUserSessionIssuerCimdClientsInfinite,
};

export type OrganizationUserSessionIssuerCimdClientsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listCimdClients organizationUserSessionIssuers
 *
 * @remarks
 * List custom CIMD document URLs on an organization-owned user_session_issuer. Requires org:read.
 */
export function useOrganizationUserSessionIssuerCimdClients(
  request: ListOrganizationUserSessionIssuerCimdClientsRequest,
  security?: ListOrganizationUserSessionIssuerCimdClientsSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationUserSessionIssuerCimdClientsQueryData,
    OrganizationUserSessionIssuerCimdClientsQueryError
  >,
): UseQueryResult<
  OrganizationUserSessionIssuerCimdClientsQueryData,
  OrganizationUserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationUserSessionIssuerCimdClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listCimdClients organizationUserSessionIssuers
 *
 * @remarks
 * List custom CIMD document URLs on an organization-owned user_session_issuer. Requires org:read.
 */
export function useOrganizationUserSessionIssuerCimdClientsSuspense(
  request: ListOrganizationUserSessionIssuerCimdClientsRequest,
  security?: ListOrganizationUserSessionIssuerCimdClientsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationUserSessionIssuerCimdClientsQueryData,
    OrganizationUserSessionIssuerCimdClientsQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationUserSessionIssuerCimdClientsQueryData,
  OrganizationUserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationUserSessionIssuerCimdClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listCimdClients organizationUserSessionIssuers
 *
 * @remarks
 * List custom CIMD document URLs on an organization-owned user_session_issuer. Requires org:read.
 */
export function useOrganizationUserSessionIssuerCimdClientsInfinite(
  request: ListOrganizationUserSessionIssuerCimdClientsRequest,
  security?: ListOrganizationUserSessionIssuerCimdClientsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
    OrganizationUserSessionIssuerCimdClientsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
    OrganizationUserSessionIssuerCimdClientsPageParams
  >,
  OrganizationUserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
    OrganizationUserSessionIssuerCimdClientsQueryError,
    InfiniteData<
      OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
      OrganizationUserSessionIssuerCimdClientsPageParams
    >,
    QueryKey,
    OrganizationUserSessionIssuerCimdClientsPageParams
  >({
    ...buildOrganizationUserSessionIssuerCimdClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listCimdClients organizationUserSessionIssuers
 *
 * @remarks
 * List custom CIMD document URLs on an organization-owned user_session_issuer. Requires org:read.
 */
export function useOrganizationUserSessionIssuerCimdClientsInfiniteSuspense(
  request: ListOrganizationUserSessionIssuerCimdClientsRequest,
  security?: ListOrganizationUserSessionIssuerCimdClientsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
    OrganizationUserSessionIssuerCimdClientsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
    OrganizationUserSessionIssuerCimdClientsPageParams
  >,
  OrganizationUserSessionIssuerCimdClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
    OrganizationUserSessionIssuerCimdClientsQueryError,
    InfiniteData<
      OrganizationUserSessionIssuerCimdClientsInfiniteQueryData,
      OrganizationUserSessionIssuerCimdClientsPageParams
    >,
    QueryKey,
    OrganizationUserSessionIssuerCimdClientsPageParams
  >({
    ...buildOrganizationUserSessionIssuerCimdClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setOrganizationUserSessionIssuerCimdClientsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userSessionIssuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: OrganizationUserSessionIssuerCimdClientsQueryData,
): OrganizationUserSessionIssuerCimdClientsQueryData | undefined {
  const key = queryKeyOrganizationUserSessionIssuerCimdClients(...queryKeyBase);

  return client.setQueryData<OrganizationUserSessionIssuerCimdClientsQueryData>(
    key,
    data,
  );
}

export function invalidateOrganizationUserSessionIssuerCimdClients(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userSessionIssuerId: string;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "listCimdClients",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationUserSessionIssuerCimdClients(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "listCimdClients",
    ],
  });
}
